import { useMutation, useQueryClient } from '@tanstack/react-query';

import { downloadBlob } from '#lib/downloadBlob';

import { exportCampaignToCSV } from '../../api/campaignCsvExportApi';
import { rescheduleSkippedRecipients, retryFailedSends } from '../../api/campaignStatisticsApi';

import { emailSendsKeys, emailSendsMutationKeys } from './keys';

/**
 * Re-schedules a campaign's failed sends
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useRetryFailedSends() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (campaignId) => retryFailedSends(campaignId),
		onSuccess: (_data, campaignId) => {
			qc.invalidateQueries({ queryKey: emailSendsKeys.stats(campaignId) });
			qc.invalidateQueries({ queryKey: emailSendsKeys.sendsByCampaign(campaignId) });
		},
	});
}

export function useExportCampaignCSV() {
	return useMutation({
		mutationKey: emailSendsMutationKeys.exportCsv,
		mutationFn: ({ campaignId, options }) => exportCampaignToCSV(campaignId, options),
		onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
	});
}

/**
 * Schedules recipients that were skipped for missing custom-column values (once values or
 * fallbacks exist).
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useRescheduleSkippedRecipients() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: emailSendsMutationKeys.rescheduleSkipped,
		mutationFn: (campaignId) => rescheduleSkippedRecipients(campaignId),
		onSuccess: (_data, campaignId) => {
			qc.invalidateQueries({ queryKey: emailSendsKeys.stats(campaignId) });
			qc.invalidateQueries({ queryKey: emailSendsKeys.customVariables(campaignId) });
		},
	});
}
